import * as yup from 'yup'
import { useFormik } from 'formik'
import classNames from 'classnames'
import { shallowEqual } from 'react-redux'
import { FC, useCallback, useEffect, useState } from 'react'

import { toast } from './base/Toast'
import Button from './base/forms/Button'
import PhoneInput from './base/PhoneInput'
import InputGroup from './base/forms/InputGroup'
import { CheckoutFormValuesType } from 'common/types/responses'
import ContentContainer from './project/tables/ContentContainer'
import { useDefaultSubscription } from 'common/services/useDefaultSubscription'
import { useChangeForwardAddressMutation } from 'common/services/useAddress'

const schema = yup.object().shape({
  first_name: yup.string().required('Required'),
  last_name: yup.string().required('Required'),
  phone_number: yup.string().required('Required'),
})

const CreateEditCheckoutForm: FC = () => {
  const [allTouched, setAllTouched] = useState(false)
  const { subscriptionDetail } = useDefaultSubscription()
  const [updateAddress, { error, isLoading }] = useChangeForwardAddressMutation(
    {},
  )
  const firstName = subscriptionDetail?.contact_name
    ?.split(' ')
    .slice(0, -1)
    .join(' ') // first name is all items before the last in the array

  const lastName = subscriptionDetail?.contact_name
    ?.split(' ')
    .slice(-1)
    .join(' ') // last name is last item in array

  const initialValues = {
    first_name: firstName || '',
    last_name: lastName || '',
    phone_number: subscriptionDetail?.contact_telephone || '',
  }
  const { errors, handleChange, handleSubmit, setValues, values } =
    useFormik<CheckoutFormValuesType>({
      initialValues,
      onSubmit: (values) => {
        updateAddress({
          first_name: values.first_name,
          last_name: values.last_name,
          subscription_id: subscriptionDetail.id,
        }).then((res) => {
          if (!res.error) {
            setAllTouched(true)
            toast(null, 'Account updated')
          }
        })
      },
      validationSchema: schema,
    })

  const getValues = useCallback(() => {
    return initialValues
  }, [subscriptionDetail?.id])

  useEffect(() => {
    setValues(getValues())
  }, [getValues])

  const hasChanged = !shallowEqual(getValues(), values)

  return (
    <form onSubmit={handleSubmit}>
      <ContentContainer>
        <div className='d-flex flex-column gap-4'>
          <h2 className='mb-0 fw-semibold'>Business Contact Information</h2>
          <div className='row gap-4'>
            <div className='col-lg-4'>
              <InputGroup
                className='flex-1'
                title='Full legal first and middle names'
                required
                isValid={!!values.first_name}
                value={values.first_name}
                onChange={handleChange}
                name='first_name'
                id='first_name'
                touched={allTouched}
              />
            </div>
            <div className='col-lg-4'>
              <InputGroup
                className='flex-1'
                title='Full legal last names(s)'
                required
                isValid={!!values.last_name}
                value={values.last_name}
                onChange={handleChange}
                name='last_name'
                id='last_name'
                touched={allTouched}
              />
            </div>
          </div>
          <div className='row'>
            <div className='col-lg-4'>
              <InputGroup
                className='flex-1 mb-2'
                title='Contact Telephone Number'
                isValid={!errors.phone_number && !!values.last_name}
                component={
                  <PhoneInput
                    disabled
                    required
                    touched={allTouched}
                    value={values.phone_number}
                    onChange={(e) => handleChange('phone_number', e)}
                  />
                }
              />
            </div>
          </div>
          <div className='d-flex gap-3 justify-content-end'>
            <Button
              className={classNames('px-10', {
                'opacity-0 pointer-events-none': !hasChanged,
              })}
              onClick={() => {
                setValues(initialValues)
              }}
              type='button'
              theme='darkOutline'
              disabled={false}
            >
              Cancel
            </Button>
            <Button
              disabled={!hasChanged || isLoading}
              className='px-10'
              type='submit'
            >
              Update
            </Button>
          </div>
        </div>
      </ContentContainer>
    </form>
  )
}

export default CreateEditCheckoutForm
